import FingerprintJS from '@fingerprintjs/fingerprintjs'
import loginByFacebook from 'api/loginByFacebook'
import loginByGoogle from 'api/loginByGoogle'
import { PlausibleEvent, RegistrationTypes } from 'enums/plausible'
import ErrorCode from 'forms/ErrorCodes'
import handleResponseError from 'forms/handleResponseError'
import RegisterForm from 'forms/RegisterForm'
import validateMessage from 'forms/validateMessage'
import { FC, useCallback, useState } from 'react'
import { clearReferralCookies } from 'utils/getReferralCookies'
import useLanguageForSave from 'utils/hooks/useLanguageForSave'
import usePlausible from 'utils/hooks/usePlausible'
import setTokensAndRedirect from 'utils/setTokensAndRedirect'

import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'

import ErrorMessage from 'components/ErrorMessage/ErrorMessage'
import FacebookAuth from 'components/FacebookAuth'
import GoogleAuth from 'components/GoogleAuth/GoogleAuth'
import { EmailIcon, WiFiIcon } from 'components/Images/Icons'

import styles from './RegisterFormOrGoogle.module.scss'

const RegisterFormOrGoogle = ({ referrer }) => {
  const router = useRouter()
  const [socialRegistrationError, setSocialRegistrationError] = useState<{
    icon?: FC
    message: string
  }>()
  const [registrationError, setRegistrationError] = useState<{ icon?: FC; message: string }>()

  const setEvent = usePlausible()

  const [t, i18n] = useTranslation(['main'])
  const savedLanguage = useLanguageForSave(i18n)

  const onGettingSocialToken = useCallback(
    async ({ providerUserToken }: { providerUserToken: string }, social: 'google' | 'facebook') => {
      try {
        setEvent(PlausibleEvent.SubmitRegister, {
          type: `user`,
          referrer,
          appName: 'mainLanding',
        })
        setEvent(PlausibleEvent.ClickedOnSocialSignUp, {
          step: 2,
          referrer,
          source: router.query?.from as string,
          social,
        })

        const fp = await FingerprintJS.load()
        const fpResponse = await fp.get()

        const loginBySocialRequest = {
          google: loginByGoogle,
          facebook: loginByFacebook,
        }

        const res = await loginBySocialRequest[social]({
          providerUserToken,
          language: savedLanguage,
          visitorId: fpResponse.visitorId,
        })

        if (res.status === 200) {
          if (localStorage.getItem('registeringFromPopup')) {
            const propType = localStorage.getItem('registeringFromPopup')
            setEvent(PlausibleEvent.RegAfterClickOnPopup, {
              popupType: propType,
              referrer,
              locale: router.locale,
              domain: window.location.host,
            })
            localStorage.removeItem('registeringFromPopup')
          }

          await Promise.all([
            setEvent(
              PlausibleEvent.UserRegistered,
              {
                userId: res.data.payload?.id,
                referrer,
                source: window.location.host,
                type: RegistrationTypes.Google,
              },
              true,
            ),
            setEvent(
              PlausibleEvent.Registered,
              {
                type: 'system',
                step: 3,
                referrer,
                source: router.query?.from as string,
                userCurrency: res.data.payload?.currency,
              },
              true,
            ),
          ])
          clearReferralCookies()
          setTokensAndRedirect(res.data.payload.accessToken, res.data.payload.refreshToken)
        } else if (res.status === 404) {
          router.push(
            `/auth/${social}/${providerUserToken}?from=${router.query?.from}`,
            `/auth/${social}/${providerUserToken}`,
          )
        } else {
          const { code, payload } = res.data
          switch (code) {
            case ErrorCode.VALIDATION_ERRORS:
              if (payload?.errors) {
                const wrappedErrors = handleResponseError(res)
                if (wrappedErrors.email) {
                  setSocialRegistrationError({ icon: EmailIcon, message: wrappedErrors.email[0] })
                } else {
                  setSocialRegistrationError({ message: wrappedErrors })
                }
              }
              break
            case ErrorCode.EMAIL_ALREADY_REGISTER:
              setSocialRegistrationError({
                icon: EmailIcon,
                message: validateMessage.emailIsAlreadyRegistered,
              })
              break
            case ErrorCode.GEO_RESTRICTED:
              setSocialRegistrationError({ message: validateMessage.restricted })
              break
            case ErrorCode.SOCIAL_DUPLICATE_USER_EMAIL:
              setSocialRegistrationError({
                icon: EmailIcon,
                message: validateMessage.duplicateUserEmail,
              })
              break
            default:
              setSocialRegistrationError({ message: validateMessage.serverError })
          }
          setEvent(PlausibleEvent.NotRegistered, {
            type: 'system',
            step: 3,
            referrer,
            source: router.query?.from as string,
            error: ErrorCode[code] || 'unknown',
            userCurrency: res.data.payload?.currency,
          })
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e)

        setSocialRegistrationError({ icon: WiFiIcon, message: validateMessage.serverError })
      }
    },
    [router, setSocialRegistrationError, savedLanguage],
  )
  return (
    <div className={styles.grid}>
      <h1 className={styles.h1}>{t('main:signUpCreateYourAcc')}</h1>
      <div className={styles.oauth} onClick={() => setRegistrationError(undefined)}>
        {socialRegistrationError && (
          <ErrorMessage
            icon={socialRegistrationError.icon}
            message={socialRegistrationError.message}
          />
        )}

        <GoogleAuth
          id="register-form-google-button"
          buttonText={t('main:signUpCreateYourAcc')}
          callback={(response) => onGettingSocialToken(response, 'google')}
        />
        {process.env.NEXT_PUBLIC_FACEBOOK_APP_ID && (
          <FacebookAuth
            id="register-form-facebook-button"
            buttonText={t('main:signUpCreateYourAcc')}
            onAuthSuccess={(response) => onGettingSocialToken(response, 'facebook')}
          />
        )}
      </div>
      <div className={styles.divider}>
        <div className={styles.line} />
        <div className={styles.text}>{t('main:signUpUseEmailToSignUp')}</div>
        <div className={styles.line} />
      </div>
      <div className={styles.form}>
        <RegisterForm
          referrer={referrer}
          error={registrationError}
          setError={setRegistrationError}
          setSocialRegistrationError={setSocialRegistrationError}
        />
      </div>
    </div>
  )
}

export default RegisterFormOrGoogle
